import { Injectable } from '@angular/core';

/**
 * Date helper service for NZ date formatting and manipulation
 * Replaces moment.js with native JavaScript Date handling
 */
@Injectable({
  providedIn: 'root'
})
export class DateHelperService {

  /**
   * Format date as YYYY-MM-DD (ISO format)
   */
  formatToISO(date: Date | string | null): string | null {
    if (!date) return null;
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  }

  /**
   * Format date as YYYY-MM-DD HH:mm:ss
   */
  formatToDateTime(date: Date | string | null): string | null {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * Format date as readable NZ format: dddd, Do MMMM YYYY
   * e.g., "Monday, 15th January 2024"
   */
  formatToNZReadable(date: Date | string | null): string | null {
    if (!date) return null;
    const d = new Date(date);
    const formatter = new Intl.DateTimeFormat('en-NZ', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    return formatter.format(d);
  }

  /**
   * Get today's date as Date object
   */
  getToday(): Date {
    return new Date();
  }

  /**
   * Get tomorrow's date
   */
  getTomorrow(): Date {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  }

  /**
   * Add days to a date
   */
  addDays(date: Date | string, days: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  /**
   * Add months to a date
   * Handles edge cases where the target month has fewer days
   */
  addMonths(date: Date | string, months: number): Date {
    const d = new Date(date);
    const targetMonth = d.getMonth() + months;
    const targetDate = new Date(d.getFullYear(), targetMonth, 1);
    
    // Get the last day of the target month
    const lastDayOfTargetMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0).getDate();
    
    // Use the original day or the last day of the target month (whichever is smaller)
    const day = Math.min(d.getDate(), lastDayOfTargetMonth);
    
    return new Date(targetDate.getFullYear(), targetDate.getMonth(), day);
  }

  /**
   * Get the first day of the month for a given date
   */
  getFirstDayOfMonth(date: Date | string): Date {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }

  /**
   * Get the last day of the month for a given date
   */
  getLastDayOfMonth(date: Date | string): Date {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
  }

  /**
   * Set time on a date (10:00:00 format)
   */
  setTime(date: Date | string, hours: number = 10, minutes: number = 0, seconds: number = 0): Date {
    const d = new Date(date);
    d.setHours(hours, minutes, seconds, 0);
    return d;
  }

  /**
   * Get Unix timestamp (seconds since epoch)
   */
  getUnixTimestamp(): number {
    return Math.floor(Date.now() / 1000);
  }

  /**
   * Get difference in minutes between two timestamps
   */
  getTimeDiffMinutes(unixTimestamp1: number, unixTimestamp2: number = this.getUnixTimestamp()): number {
    return Math.floor((unixTimestamp2 - unixTimestamp1) / 60);
  }

  /**
   * Parse ISO string to Date
   */
  parseISO(isoString: string | null): Date | null {
    if (!isoString) return null;
    return new Date(isoString);
  }

  /**
   * Convert Date to Date (just for consistency with moment().toDate())
   */
  toDate(date: Date | string | null): Date | null {
    if (!date) return null;
    return new Date(date);
  }
}
